import { useState, useMemo, useEffect } from 'react';
import { ThemeProvider, CssBaseline, useMediaQuery, Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { MobileDrawer } from './components/layout/MobileDrawer';
import { ShipConsole } from './components/console';
import { ConsoleProvider } from './contexts/ConsoleContext';
import { usePlayerId } from './hooks/usePlayerId';
import { fullReset } from './services/gameInitialization';
import { DATABASE_SERVICE_URL } from './services/workerUrls';

// Light palette
const lightPalette = {
  mode: 'light' as const,
  primary: {
    main: '#0891b2',
    light: '#22d3ee',
    dark: '#0e7490',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#f97316',
    light: '#fb923c',
    dark: '#ea580c',
    contrastText: '#FFFFFF',
  },
  error: { main: '#dc2626', light: '#f87171', dark: '#b91c1c' },
  warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
  info: { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb' },
  success: { main: '#10b981', light: '#34d399', dark: '#059669' },
  background: { default: '#f0f9ff', paper: '#FFFFFF' },
  text: { primary: '#0f172a', secondary: '#475569' },
  divider: '#cbd5e1',
};

// Dark palette
const darkPalette = {
  mode: 'dark' as const,
  primary: {
    main: '#22d3ee',
    light: '#67e8f9',
    dark: '#06b6d4',
    contrastText: '#0f172a',
  },
  secondary: {
    main: '#fb923c',
    light: '#fdba74',
    dark: '#f97316',
    contrastText: '#0f172a',
  },
  error: { main: '#f87171', light: '#fca5a5', dark: '#dc2626' },
  warning: { main: '#fbbf24', light: '#fcd34d', dark: '#f59e0b' },
  info: { main: '#60a5fa', light: '#93c5fd', dark: '#3b82f6' },
  success: { main: '#34d399', light: '#6ee7b7', dark: '#10b981' },
  background: { default: '#0a0f1a', paper: '#111827' },
  text: { primary: '#e2e8f0', secondary: '#94a3b8' },
  divider: '#1e293b',
};

export type ThemeMode = 'light' | 'dark' | 'auto';

const THEME_STORAGE_KEY = 'space-antics-theme';

function getStoredTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'auto') {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return 'dark';
}

function App() {
  // Theme mode: light, dark, or auto (follows system preference)
  const [themeMode, setThemeMode] = useState<ThemeMode>(getStoredTheme);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Player ID management with localStorage persistence
  const { playerId, isLoading: playerLoading, error: playerError } = usePlayerId();

  // Log app startup information
  useEffect(() => {
    console.log("[APP] === SPACE ANTICS INITIALIZED ===");
    console.log("[APP] Environment:", import.meta.env.MODE);
    console.log("[APP] Database URL:", DATABASE_SERVICE_URL());
    console.log("[APP] Player ID:", playerId || "loading...");
    console.log("[APP] Theme mode:", themeMode);
  }, [playerId, themeMode]);

  // Detect system preference
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  // Determine if we should use dark mode
  const isDarkMode = themeMode === 'auto' ? prefersDark : themeMode === 'dark';

  const theme = useMemo(
    () =>
      createTheme({
        palette: isDarkMode ? darkPalette : lightPalette,
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        },
        shape: { borderRadius: 8 },
        components: {
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                backgroundImage: 'none',
                ...(isDarkMode && {
                  boxShadow: '0 0 0 1px rgba(34, 211, 238, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                }),
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: { borderRadius: 6, textTransform: 'none', fontWeight: 500 },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: { borderRadius: 4, fontWeight: 500 },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: { backgroundImage: 'none' },
            },
          },
          MuiLinearProgress: {
            styleOverrides: {
              root: { borderRadius: 4, backgroundColor: 'rgba(148, 163, 184, 0.2)' },
              bar: { borderRadius: 4 },
            },
          },
          MuiTabs: {
            styleOverrides: {
              root: { minHeight: 40 },
              indicator: { height: 3, borderRadius: '3px 3px 0 0' },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: { minHeight: 40, textTransform: 'none', fontWeight: 500 },
            },
          },
        },
      }),
    [isDarkMode]
  );

  const handleThemeModeChange = (mode: ThemeMode) => {
    setThemeMode(mode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // localStorage not available
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const toggleDebugMode = () => {
    setDebugMode((prev) => !prev);
  };

  const resetGame = async () => {
    setIsResetting(true);
    setResetError(null);

    const result = await fullReset();

    if (result.success) {
      // Reload page to restart with fresh data
      window.location.reload();
    } else {
      setResetError(result.error || 'Unknown error occurred');
      setIsResetting(false);
    }
  };

  // Show loading or error state while initializing player
  if (playerLoading || !playerId) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: 2,
            backgroundColor: 'background.default',
            px: 3,
            textAlign: 'center',
          }}
        >
          {playerError ? (
            <>
              <Alert severity="error" sx={{ maxWidth: 600, mb: 3 }}>
                <Typography variant="h6" component="div" gutterBottom>
                  Initialization Failed
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1, mb: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {playerError}
                </Typography>
              </Alert>
              {resetError && (
                <Alert severity="error" sx={{ maxWidth: 600, mb: 2 }} onClose={() => setResetError(null)}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {resetError}
                  </Typography>
                </Alert>
              )}
              <Alert severity="info" sx={{ maxWidth: 600, mb: 3 }}>
                <Typography variant="body2">
                  <strong>Try these steps:</strong>
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  1. Click "Reset Game" below to wipe and reinitialize
                  <br />
                  2. If that fails, use Database Admin Panel (☰ menu) → Wipe All Data → Seed Galaxy → Initialize Player
                  <br />
                  3. Check Cloudflare Dashboard logs for detailed errors
                </Typography>
              </Alert>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={resetGame}
                disabled={isResetting}
                startIcon={isResetting ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{ minWidth: 200 }}
              >
                {isResetting ? 'Resetting Game...' : 'Reset Game'}
              </Button>
            </>
          ) : (
            <>
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary">
                Initializing player...
              </Typography>
            </>
          )}
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ConsoleProvider debugMode={debugMode}>
        <MobileDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          themeMode={themeMode}
          onThemeModeChange={handleThemeModeChange}
          onDebugToggle={toggleDebugMode}
        />
        <ShipConsole
          playerId={playerId}
          onMenuClick={handleDrawerToggle}
          themeMode={themeMode}
          onThemeModeChange={handleThemeModeChange}
          onDebugToggle={toggleDebugMode}
        />
      </ConsoleProvider>
    </ThemeProvider>
  );
}

export default App;
